import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiImage, FiAlertCircle, FiCheckCircle, FiX, FiRefreshCw } = FiIcons;

// Removed size limit to allow any image size
const ALLOWED_FORMATS = [
  'image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp', 
  'image/svg+xml', 'image/bmp', 'image/tiff'
];

const ImageUploader = ({ currentImageUrl, onImageChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(currentImageUrl || '');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);
  const urlTimeoutRef = useRef(null);

  // Update preview when currentImageUrl changes from parent
  React.useEffect(() => {
    if (currentImageUrl !== preview) {
      setPreview(currentImageUrl || '');
      if (currentImageUrl) {
        checkImageDimensions(currentImageUrl);
      } else {
        setDimensions(null);
      }
    }
  }, [currentImageUrl]);

  const checkImageDimensions = (imageUrl) => {
    if (!imageUrl) return;
    const img = new Image();
    img.onload = () => {
      setDimensions({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      console.error('Failed to load image for dimensions check');
    };
    img.src = imageUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const validateAndProcessFile = (file) => {
    setError(null);
    setUploadSuccess(false);

    // Check file format
    if (!ALLOWED_FORMATS.includes(file.type)) {
      setError(`Unsupported format: ${file.type}. Please use a common image format.`);
      return;
    }

    // Process the file
    setIsLoading(true);

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      // Create an image element to check dimensions
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        setDimensions({ width, height });

        // Set preview and pass the base64 string to parent component
        setPreview(reader.result);
        onImageChange(reader.result);
        setIsLoading(false);
        setUploadSuccess(true);

        // Reset success message after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      };

      img.onerror = () => {
        setError('Failed to load image');
        setIsLoading(false);
      };

      img.src = reader.result;
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setIsLoading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreview('');
    onImageChange('');
    setDimensions(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (urlInputRef.current) {
      urlInputRef.current.value = '';
    }
  };

  // Function to check if a URL is an image
  const isImageUrl = async (url) => {
    // Common image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff'];
    
    // Check URL extension
    const urlLower = url.toLowerCase();
    if (imageExtensions.some(ext => urlLower.endsWith(ext))) {
      return true;
    }
    
    // For URLs without extensions, try to fetch headers
    try {
      // Create a new AbortController
      const controller = new AbortController();
      const signal = controller.signal;
      
      // Set a timeout to abort the fetch after 3 seconds
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch(url, {
        method: 'HEAD',
        signal,
        mode: 'no-cors' // Try with no-cors mode to avoid CORS issues
      });
      
      clearTimeout(timeoutId);
      
      // If we got a response, check the content type
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        return contentType && contentType.startsWith('image/');
      }
      
      return false;
    } catch (error) {
      console.log('Error checking image URL:', error);
      // If there's an error (like CORS), assume it might be an image
      // since we already checked the extension
      return true;
    }
  };

  const handleExternalUrlChange = async (e) => {
    const url = e.target.value;
    
    // Clear any existing timeout to prevent multiple checks
    if (urlTimeoutRef.current) {
      clearTimeout(urlTimeoutRef.current);
    }
    
    if (!url) {
      setPreview('');
      setDimensions(null);
      onImageChange('');
      return;
    }
    
    // Set a small delay to avoid checking on every keystroke
    urlTimeoutRef.current = setTimeout(async () => {
      setIsCheckingUrl(true);
      setError(null);
      
      // Check if URL is valid
      let validUrl;
      try {
        validUrl = new URL(url);
      } catch (e) {
        try {
          // Try adding https:// if missing
          validUrl = new URL(`https://${url}`);
        } catch (e) {
          setError('Please enter a valid URL');
          setIsCheckingUrl(false);
          return;
        }
      }
      
      // Normalize the URL
      const normalizedUrl = validUrl.href;
      
      try {
        // Check if it's actually an image URL
        const isImage = await isImageUrl(normalizedUrl);
        
        if (!isImage) {
          // If it's not a direct image URL, try to use it as a webpage and fetch a screenshot
          const domain = validUrl.hostname;
          const screenshotUrl = `https://image.thum.io/get/width/1200/crop/800/auth/9036-${Math.random().toString(36).substring(2,9)}/https://${domain}${validUrl.pathname}`;
          
          // Test if we can load the screenshot
          const screenshotImg = new Image();
          screenshotImg.onload = () => {
            setPreview(screenshotUrl);
            onImageChange(screenshotUrl);
            checkImageDimensions(screenshotUrl);
            setIsCheckingUrl(false);
            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000);
          };
          screenshotImg.onerror = () => {
            // If screenshot fails, use the original URL anyway as a fallback
            setPreview(normalizedUrl);
            onImageChange(normalizedUrl);
            checkImageDimensions(normalizedUrl);
            setIsCheckingUrl(false);
          };
          screenshotImg.src = screenshotUrl;
        } else {
          // It is an image URL, use it directly
          const img = new Image();
          img.onload = () => {
            setPreview(normalizedUrl);
            onImageChange(normalizedUrl);
            checkImageDimensions(normalizedUrl);
            setIsCheckingUrl(false);
            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000);
          };
          img.onerror = () => {
            setError('Failed to load image from URL');
            setIsCheckingUrl(false);
          };
          img.src = normalizedUrl;
        }
      } catch (error) {
        console.error('Error processing image URL:', error);
        setError('Could not process this URL');
        setIsCheckingUrl(false);
      }
    }, 500);
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Banner Image *
      </label>
      
      {preview ? (
        <div className="relative mb-2 border rounded-lg overflow-hidden">
          <img
            src={preview}
            alt="Banner preview"
            className="w-full h-48 object-cover"
            onError={(e) => {
              setError('Error loading image');
              setPreview('');
              onImageChange('');
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
            {dimensions ? `${dimensions.width} × ${dimensions.height}px` : 'Loading dimensions...'}
          </div>
          <button
            onClick={handleRemoveImage}
            className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white p-1 rounded-full hover:bg-opacity-100 transition-opacity"
            title="Remove image"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon
                icon={isLoading ? FiImage : FiUpload}
                className={`text-xl ${isLoading ? 'animate-pulse text-blue-500' : 'text-gray-500'}`}
              />
            </div>
            <p className="font-medium text-gray-800">
              {isLoading ? 'Processing...' : 'Drag & drop banner here'}
            </p>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              Any image format supported
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current.click()}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isLoading
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } transition-colors`}
            >
              Upload Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-red-600 text-sm"
        >
          <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      {uploadSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md flex items-center text-green-600 text-sm"
        >
          <SafeIcon icon={FiCheckCircle} className="mr-2 flex-shrink-0" />
          <span>Banner uploaded successfully</span>
        </motion.div>
      )}

      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Or enter image URL
        </label>
        <div className="flex">
          <input
            ref={urlInputRef}
            type="text"
            defaultValue={preview && !preview.startsWith('data:') ? preview : ''}
            onChange={handleExternalUrlChange}
            placeholder="https://example.com/image.jpg"
            className="w-full p-2 border rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            disabled={isCheckingUrl}
          />
          {isCheckingUrl && (
            <div className="ml-2 flex items-center">
              <SafeIcon icon={FiRefreshCw} className="animate-spin text-blue-500" />
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Enter any image URL or webpage URL - we'll extract the image automatically
        </p>
      </div>
    </div>
  );
};

export default ImageUploader;